import {
  Field,
  Input,
  InputNumber,
  RadioGroup,
  Spin,
  TextArea,
} from '@/components/commons';
import {
  FALLBACK_URL,
  FILE_SIZE_LIMIT,
  ImageService,
  SUPPORTED_EXTENSION,
  SUPPORTED_FORMATS,
} from '@/services/image';
import { KazokugotoService } from '@/services';
import { ENDPOINT } from '@/services/endpoint';
import { IllustrationThemeService } from '@/services/illustrationTheme';
import { notificationMessage, NOTIFY_STATUS } from '@/utils';
import { loadPreviewSample } from '../../previewSample';
import { CloseOutlined } from '@ant-design/icons';
import { Badge, Button, Checkbox, Form, Image, Upload } from 'antd';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQuery } from 'react-query';

interface IProps {
  isLoading: boolean;
  isEdit: boolean;
}

const isActiveOptions = [
  { label: '有効', value: true },
  { label: '無効', value: false },
];

export interface CoverImage {
  previewUrl: string;
  file: File;
}

export const IllustrationThemeForm: React.FC<IProps> = ({
  isLoading,
  isEdit,
}) => {
  const { watch, setValue, setError, clearErrors } = useFormContext();

  const coverImage: CoverImage | null = watch('coverImage');
  const previewImageUrl: string | null = watch('previewImageUrl');
  const thumbnailUrl = coverImage?.previewUrl || previewImageUrl;

  const referenceImageUrl: string | null = watch('referenceImageUrl');
  const referenceCharacterIds: number[] = watch('referenceCharacterIds') ?? [];

  // 合成キャラの候補はかぞくごとのキャラから選ぶ（アバター未設定のキャラは選べない）
  const { data: charactersResponse, isFetching: isFetchingCharacters } =
    useQuery([ENDPOINT.KAZOKUGOTO.LIST_CHARACTERS], () =>
      KazokugotoService.listCharacters(),
    );
  const characters = charactersResponse?.data ?? [];
  const selectableCharacterIds = characters
    .filter((c) => !!c.avatarUrl)
    .map((c) => c.id);

  const toggleCharacter = (id: number, checked: boolean) => {
    setValue(
      'referenceCharacterIds',
      checked
        ? [...referenceCharacterIds, id]
        : referenceCharacterIds.filter((v) => v !== id),
    );
  };

  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResultUrl, setPreviewResultUrl] = useState<string>('');
  const [previewCharacterId, setPreviewCharacterId] = useState<number | null>(
    null,
  );
  const [referenceUploading, setReferenceUploading] = useState(false);

  const beforeUpload = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (file.size > FILE_SIZE_LIMIT) {
      setError('thumbnail', {
        type: 'validate',
        message: '画像のサイズは10MBを超えています。',
      });
      return Upload.LIST_IGNORE;
    }

    if (
      !SUPPORTED_EXTENSION.includes(fileExtension || '') ||
      !SUPPORTED_FORMATS.includes(file.type)
    ) {
      setError('thumbnail', {
        type: 'validate',
        message: '画像は .jpg、.png、.jpeg 形式ではありません。',
      });
      return Upload.LIST_IGNORE;
    }
    clearErrors('thumbnail');
    handleUpload(file);

    return true;
  };

  const handleUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setValue('coverImage', {
        previewUrl: reader.result as string,
        file,
      });
    };
    reader.readAsDataURL(file);
  };

  // 合成キャラ画像は選択時に即アップロードして URL をフォーム値に保持する。
  // テスト生成・保存の両方が同じ URL を参照できるようにするため。
  const beforeUploadReference = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (file.size > FILE_SIZE_LIMIT) {
      setError('referenceImage', {
        type: 'validate',
        message: '画像のサイズは10MBを超えています。',
      });
      return Upload.LIST_IGNORE;
    }

    if (
      !SUPPORTED_EXTENSION.includes(fileExtension || '') ||
      !SUPPORTED_FORMATS.includes(file.type)
    ) {
      setError('referenceImage', {
        type: 'validate',
        message: '画像は .jpg、.png、.jpeg 形式ではありません。',
      });
      return Upload.LIST_IGNORE;
    }
    clearErrors('referenceImage');
    handleReferenceUpload(file);

    return true;
  };

  const handleReferenceUpload = async (file: File) => {
    try {
      setReferenceUploading(true);
      const res = await ImageService.uploadFile(file);
      setValue('referenceImageUrl', res?.data?.url ?? '');
    } catch (error) {
      notificationMessage({
        message: '合成キャラ画像のアップロードに失敗しました。',
        type: NOTIFY_STATUS.ERROR,
      });
    } finally {
      setReferenceUploading(false);
    }
  };

  const handlePreview = async () => {
    const prompt = watch('prompt');
    if (!prompt?.trim()) {
      setError('prompt', {
        type: 'validate',
        message: 'プロンプトは未入力です。',
      });
      return;
    }
    try {
      setPreviewLoading(true);
      const res = await IllustrationThemeService.previewImage({
        prompt,
        samplePayload: loadPreviewSample(),
        // 合成キャラ（候補 or 画像）が設定されていれば本番投稿と同じ合成生成でテストする
        referenceCharacterIds,
        ...(referenceImageUrl ? { referenceImageUrl } : {}),
        ...(referenceCharacterIds.length || referenceImageUrl
          ? {
              // 空欄ならサーバー側でプロンプト管理の共通指示が使われる
              referenceCharacterPrompt: watch('referenceCharacterPrompt') ?? '',
            }
          : {}),
      });
      setPreviewResultUrl(res?.data?.url);
      setPreviewCharacterId(res?.data?.referenceCharacterId ?? null);
    } catch (error) {
      notificationMessage({
        message: 'プレビュー画像の生成に失敗しました。',
        type: NOTIFY_STATUS.ERROR,
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <Form layout='vertical'>
      <Spin spinning={isLoading}>
        <Field label='コード（styleName）' name='code' required>
          <Input
            placeholder='normal'
            maxLength={255}
            disabled={isEdit}
          />
        </Field>
        {isEdit && (
          <p className='text-xs text-gray-500 mt-[-12px] mb-3'>
            ※ コードは過去投稿との整合のため変更できません。
          </p>
        )}

        <Field label='表示名' name='labelJp' required>
          <Input placeholder='ポップ' maxLength={255} />
        </Field>

        <Field label='プロンプト' name='prompt' required>
          <TextArea placeholder='画像生成プロンプト' rows={6} />
        </Field>

        <div className='mb-4'>
          <Button
            type='default'
            onClick={handlePreview}
            loading={previewLoading}
            className='text-[#84ab42] border border-[#84ab42] font-semibold'
          >
            この画風でテスト生成
          </Button>
          {previewResultUrl && (
            <div className='pt-2'>
              <Image
                height={200}
                src={previewResultUrl}
                className='block rounded-lg'
                fallback={FALLBACK_URL}
              />
              {previewCharacterId && (
                <p className='text-xs text-gray-500 mt-1'>
                  合成したキャラ：
                  {characters.find((c) => c.id === previewCharacterId)?.name ??
                    `ID ${previewCharacterId}`}
                </p>
              )}
            </div>
          )}
        </div>

        <label className='title-calendar font-semibold'>サムネイル画像</label>
        <div className='flex items-baseline gap-2 pt-2 mb-4'>
          {!thumbnailUrl ? (
            <div className='border-solid rounded border-2 border-zinc-300 w-[200px] h-[100px] flex justify-center items-center'>
              <Upload
                beforeUpload={(file: File) => beforeUpload(file)}
                customRequest={() => {}}
                listType='text'
                maxCount={1}
                showUploadList={false}
              >
                <Button
                  type='default'
                  className='text-[#84ab42] border border-[#84ab42] font-semibold'
                >
                  画像を選択
                </Button>
              </Upload>
            </div>
          ) : (
            <div className='relative inline-block'>
              <Image
                height={100}
                src={thumbnailUrl}
                className='block rounded-lg'
                fallback={FALLBACK_URL}
              />
              <Button
                type='primary'
                shape='circle'
                icon={<CloseOutlined />}
                size='small'
                onClick={() => {
                  setValue('coverImage', null);
                  setValue('previewImageUrl', '');
                }}
                className='absolute -top-2 -right-2 z-10 bg-red-500 border-none'
              />
            </div>
          )}
        </div>
        <Field name='thumbnail' className='mt-[-20px]'>
          <></>
        </Field>

        <label className='title-calendar font-semibold'>
          合成キャラ（かぞくごとのキャラから選ぶ）
        </label>
        <p className='text-xs text-gray-500 mt-1 mb-2'>
          選んだキャラの中から、投稿ごとにランダムで1体をイラストに合成します。1体だけ選べば毎回そのキャラになります。選ぶと下の「合成キャラ画像」より優先されます。
        </p>
        <div className='flex gap-2 mb-2'>
          <Button
            size='small'
            onClick={() =>
              setValue('referenceCharacterIds', selectableCharacterIds)
            }
          >
            すべて選択
          </Button>
          <Button
            size='small'
            onClick={() => setValue('referenceCharacterIds', [])}
          >
            すべて解除
          </Button>
          <span className='text-xs text-gray-500 self-center'>
            {referenceCharacterIds.length}体を選択中
          </span>
        </div>
        <Spin spinning={isFetchingCharacters}>
          <div className='grid grid-cols-4 gap-2 mb-4'>
            {characters.map((c) => (
              <label
                key={c.id}
                className={`flex flex-col items-center gap-1 p-2 rounded border cursor-pointer ${
                  referenceCharacterIds.includes(c.id)
                    ? 'border-[#84ab42] bg-[#f4f8ec]'
                    : 'border-zinc-200'
                } ${!c.avatarUrl ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {c.avatarUrl ? (
                  <img
                    src={c.avatarUrl}
                    alt={c.name}
                    className='w-[56px] h-[56px] object-contain'
                  />
                ) : (
                  <div className='w-[56px] h-[56px] flex items-center justify-center text-[10px] text-gray-400'>
                    画像なし
                  </div>
                )}
                <Checkbox
                  checked={referenceCharacterIds.includes(c.id)}
                  disabled={!c.avatarUrl}
                  onChange={(e) => toggleCharacter(c.id, e.target.checked)}
                >
                  <span className='text-xs'>{c.name}</span>
                </Checkbox>
              </label>
            ))}
          </div>
        </Spin>

        <label className='title-calendar font-semibold'>
          合成キャラ画像（アップロード）
        </label>
        <p className='text-xs text-gray-500 mt-1 mb-2'>
          上でキャラを選んでいないときに使います。どちらも未設定なら従来どおりテキストのみで生成します。
        </p>
        <div className='flex items-baseline gap-2 pt-2 mb-4'>
          {!referenceImageUrl ? (
            <div className='border-solid rounded border-2 border-zinc-300 w-[200px] h-[100px] flex justify-center items-center'>
              <Upload
                beforeUpload={(file: File) => beforeUploadReference(file)}
                customRequest={() => {}}
                listType='text'
                maxCount={1}
                showUploadList={false}
              >
                <Button
                  type='default'
                  loading={referenceUploading}
                  className='text-[#84ab42] border border-[#84ab42] font-semibold'
                >
                  画像を選択
                </Button>
              </Upload>
            </div>
          ) : (
            <div className='relative inline-block'>
              <Image
                height={100}
                src={referenceImageUrl}
                className='block rounded-lg'
                fallback={FALLBACK_URL}
              />
              <Button
                type='primary'
                shape='circle'
                icon={<CloseOutlined />}
                size='small'
                onClick={() => setValue('referenceImageUrl', '')}
                className='absolute -top-2 -right-2 z-10 bg-red-500 border-none'
              />
            </div>
          )}
        </div>
        <Field name='referenceImage' className='mt-[-20px]'>
          <></>
        </Field>

        <Field
          label='合成キャラの指示（このテーマだけ上書き）'
          name='referenceCharacterPrompt'
        >
          <TextArea placeholder='空欄なら共通の指示を使います' rows={6} />
        </Field>
        <p className='text-xs text-gray-500 mt-[-12px] mb-4'>
          合成キャラ（選択・画像のどちらでも）を設定したテーマで、プロンプトの末尾に付ける指示です。空欄ならプロンプト管理の「家族の自由投稿作成
          - 合成キャラの指示（共通）」を使います。
        </p>

        <Field label='並び順' name='sortOrder' required>
          <InputNumber
            placeholder='並び順'
            min={0}
            className='w-[120px] bg-white'
            style={{ backgroundColor: '#fff' }}
          />
        </Field>

        <Field label='有効状態' name='isActive' className='w-full'>
          <RadioGroup options={isActiveOptions} />
        </Field>
      </Spin>
    </Form>
  );
};
